import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ReportPlanSection } from "./ReportPlanSection";
import { AssignModulesSection } from "./AssignModulesSection";
import { DataReadinessSection } from "./DataReadinessSection";
import { DataReadinessModal } from "./DataReadinessModal";
import { ModulePickerPopover } from "./ModulePickerPopover";
import { HistoryPanel } from "./HistoryPanel";
import { DataReadyRulesPanel } from "./DataReadyRulesPanel";
import { RaIcon } from "./RaIcon";
import {
  useProjectReports,
  useReportAssignmentHistory,
  useReportAssignmentWorkspace,
} from "./useReportAssignmentWorkspace";
import { apiErrorMessage, bomStats, formatDate, rowState } from "./logic";
import { ReportAssignmentRow } from "./types";
import "./reportAssignment.css";

/** Reverts a workspace action; handed to the "Undo" toast. */
type UndoAction = () => void;

interface ToastState {
  message: string;
  undo?: UndoAction;
}

type SidePanel = { kind: "history"; reportId: number | null } | { kind: "rules" } | null;

export const ReportAssignmentPage: React.FC = () => {
  const history = useHistory();
  const { projectId } = useParams<{ customerId: string; projectId: string }>();

  const workspace = useReportAssignmentWorkspace(projectId);
  const { rows, modules, boms, reportTypes, project } = workspace;
  const mapping = useProjectReports(projectId, !workspace.isLoading && !workspace.error);

  const rootRef = useRef<HTMLDivElement>(null);
  const [picker, setPicker] = useState<{ rowId: number; anchor: HTMLElement } | null>(null);
  const [statusRowId, setStatusRowId] = useState<number | null>(null);
  const [side, setSide] = useState<SidePanel>(null);
  const [flashRowId, setFlashRowId] = useState<number | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimer = useRef<number>();

  const historyQuery = useReportAssignmentHistory(projectId, side?.kind === "history");

  // The summary bar is fixed to the viewport, so keep it aligned with the
  // content area next to the app's (collapsible) navigation drawer.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const sync = () => {
      const rect = root.getBoundingClientRect();
      root.style.setProperty("--ra-left", `${rect.left}px`);
      root.style.setProperty("--ra-width", `${rect.width}px`);
    };
    sync();
    window.addEventListener("resize", sync);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    observer?.observe(root);
    return () => {
      window.removeEventListener("resize", sync);
      observer?.disconnect();
    };
  }, []);

  const showToast = useCallback((message: string, undo?: UndoAction) => {
    window.clearTimeout(toastTimer.current);
    setToast({ message, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const { undoChanges } = workspace;
  /** Toast "Undo" that reverts the given history entries through the API. */
  const undoFor = useCallback(
    (historyIds: number[]): UndoAction | undefined =>
      historyIds.length ? () => void undoChanges(historyIds) : undefined,
    [undoChanges],
  );

  const closePicker = useCallback(() => setPicker(null), []);
  const closeStatus = useCallback(() => setStatusRowId(null), []);
  const closeSide = useCallback(() => setSide(null), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setPicker(null);
      setStatusRowId(null);
      setSide(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const typeName = (typeKey: string) => reportTypes.find((t) => t.key === typeKey)?.name ?? typeKey;

  const handleConfirm = async (rowId: number) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row || row.confirmed || rowState(row) === "needs_modules") return;
    const ids = await workspace.confirmReport(rowId);
    if (ids.length) showToast(`${typeName(row.typeKey)} confirmed and locked.`, undoFor(ids));
  };

  const handleConfirmAll = async (bomId: number, ids: number[]) => {
    if (!ids.length) return;
    const entryIds = await workspace.confirmAllReadyInBom(bomId);
    if (entryIds.length) {
      const bomName = boms.find((b) => b.id === bomId)?.name ?? `BOM ${bomId}`;
      showToast(`${entryIds.length} reports confirmed in ${bomName}`, undoFor(entryIds));
    }
  };

  const pickerRow = picker ? rows.find((r) => r.id === picker.rowId) ?? null : null;

  const handleSaveModules = async (nextModuleIds: number[], reason?: string) => {
    if (!pickerRow) return;
    const row = pickerRow;
    const ids = await workspace.saveModuleSelection(row.id, nextModuleIds, reason);
    showToast(
      row.confirmed
        ? `${typeName(row.typeKey)} updated. The change is saved in the history.`
        : `Modules updated for ${typeName(row.typeKey)}. Confirm it when it looks right.`,
      undoFor(ids),
    );
  };

  const goToAssignment = (row: ReportAssignmentRow) => {
    setStatusRowId(null);
    workspace.setActiveBomId(row.bomId);
    setFlashRowId(row.id);
    window.setTimeout(() => {
      const el = rootRef.current?.querySelector(`[data-report-id="${row.id}"]`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 60);
    window.setTimeout(() => setFlashRowId(null), 1700);
  };

  const handleFinish = async () => {
    await workspace.finish();
    showToast("Report assignment finished. Reports move to the writer’s agenda as their data becomes ready.");
  };

  const overallStats = bomStats(rows);
  const allConfirmed = overallStats.total > 0 && overallStats.confirmed === overallStats.total;
  const confirmedPct = overallStats.total
    ? Math.round((overallStats.confirmed / overallStats.total) * 100)
    : 0;

  const statusRow = statusRowId !== null ? rows.find((r) => r.id === statusRowId) ?? null : null;
  const statusType = statusRow ? reportTypes.find((t) => t.key === statusRow.typeKey) ?? null : null;
  const pickerType = pickerRow ? reportTypes.find((t) => t.key === pickerRow.typeKey) ?? null : null;
  const pickerBom = pickerRow ? boms.find((b) => b.id === pickerRow.bomId) : undefined;

  const ntp = boms.map((b) => b.ntp).filter((v): v is string => !!v).sort()[0] ?? null;
  const failedInspection = modules.filter((m) => !m.passedInspection).length;
  const spares = modules.filter((m) => m.spare).length;
  const steps = [
    { n: "Modules received", d: `${modules.length} modules · ${boms.length} BOMs` },
    {
      n: "Incoming inspection",
      d: failedInspection ? `${failedInspection} did not pass` : "All modules passed",
    },
    { n: "TSD assignment", d: spares ? `${spares} spare, rest have a test` : "Every module has a test" },
    { n: "Report assignment", d: "Which modules feed each report" },
  ];

  const header = (
    <>
      <div className="ph">
        <div>
          <h1>Report assignment</h1>
          <div className="meta">
            {project ? (
              <>
                Project <b>{project.number}</b>
                {" · "}
                {project.customerName}
                {ntp && (
                  <>
                    {" · "}NTP <b>{formatDate(ntp)}</b>
                  </>
                )}
                {project.projectManager && (
                  <>
                    {" · "}PM <b>{project.projectManager}</b>
                  </>
                )}
              </>
            ) : (
              <>Project {projectId}</>
            )}
          </div>
        </div>
      </div>

      <div className="steps">
        {steps.map((s, i) => {
          const last = i === steps.length - 1;
          return (
            <div key={s.n} title={s.d} className={`st ${last ? "on" : "done"}`}>
              <span className="n">{last ? i + 1 : "✓"}</span>
              <div>
                <b>{s.n}</b>
                <span>{s.d}</span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  if (workspace.isLoading || workspace.error || workspace.needsPlan) {
    return (
      <div className="ra3" ref={rootRef}>
        <div className="main">
          {header}
          {workspace.isLoading && <div className="ra-load" role="progressbar"><i /></div>}
          {!workspace.isLoading && workspace.error && (
            <div className="ra-state err" role="alert">
              <h2>Could not load report assignment</h2>
              <p>
                {apiErrorMessage(workspace.error, "Could not load the report assignment workspace.")}
              </p>
              <button type="button" className="btn" onClick={() => workspace.refetch()}>
                Retry
              </button>
            </div>
          )}
          {!workspace.isLoading && !workspace.error && workspace.needsPlan && (
            <div className="ra-state">
              <h2>No reports apply to this project yet</h2>
              <p>
                Reports are added automatically once the project has modules with a test
                sequence. Nothing needs to be selected here.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ra3" ref={rootRef}>
      <div className="main">
        {header}

        {workspace.finished && (
          <div className="ra-state">
            <h2>Report assignment finished</h2>
            <p>
              Finished by {workspace.finished.by} on {formatDate(workspace.finished.at)}.
            </p>
          </div>
        )}

        <ReportPlanSection
          reportTypes={reportTypes}
          boms={boms}
          rows={rows}
          mapping={mapping.data}
        />

        <AssignModulesSection
          reportTypes={reportTypes}
          boms={boms}
          rows={rows}
          modules={modules}
          activeBomId={workspace.activeBomId}
          flashRowId={flashRowId}
          busy={workspace.isMutating}
          historyCountFor={(id) => rows.find((r) => r.id === id)?.historyCount ?? 0}
          totalHistoryCount={rows.reduce((n, r) => n + r.historyCount, 0)}
          onSelectBom={workspace.setActiveBomId}
          onChooseModules={(row, anchor) => setPicker({ rowId: row.id, anchor })}
          onConfirm={handleConfirm}
          onConfirmAllReady={handleConfirmAll}
          onOpenHistory={(reportId) => setSide({ kind: "history", reportId })}
        />

        <DataReadinessSection
          reportTypes={reportTypes}
          boms={boms}
          rows={rows}
          onOpenStatus={(row) => setStatusRowId(row.id)}
          onOpenSetup={() => setSide({ kind: "rules" })}
        />
      </div>

      <div className="sumbar">
        <div className="sb-l">
          <div className="sb-t">
            <b>
              {overallStats.confirmed} of {overallStats.total}
            </b>{" "}
            reports confirmed
          </div>
          <div className="prog">
            <i style={{ width: `${confirmedPct}%` }} />
          </div>
        </div>
        <div className="sb-c">
          {overallStats.needsModules > 0 && (
            <span className="chip amb">
              <RaIcon name="alert" />
              {overallStats.needsModules} needs modules
            </span>
          )}
          <span className="chip grn">
            <RaIcon name="doc" />
            {overallStats.readyToWrite} ready to write
          </span>
        </div>
        <span className="sp" />
        <span className="hint">
          {allConfirmed
            ? "All confirmed. Reports move to the writer’s agenda as their data becomes ready."
            : "A report goes to the writer’s agenda once it is confirmed and its data is ready."}
        </span>
        <button
          type="button"
          className="btn pr"
          disabled={!allConfirmed || !!workspace.finished || workspace.isFinishing}
          title={
            allConfirmed ? undefined : "Confirm every report above before finishing report assignment"
          }
          onClick={handleFinish}
        >
          <RaIcon name="check" />
          {workspace.finished ? "Report assignment finished" : "Finish report assignment"}
        </button>
      </div>

      {picker && pickerRow && pickerType && (
        <ModulePickerPopover
          key={pickerRow.id}
          anchor={picker.anchor}
          row={pickerRow}
          reportType={pickerType}
          bomName={pickerBom?.name ?? ""}
          bomModules={modules.filter((m) => m.bomId === pickerRow.bomId)}
          onSave={handleSaveModules}
          onClose={closePicker}
        />
      )}

      {statusRow && statusType && (
        <DataReadinessModal
          key={statusRow.id}
          row={statusRow}
          reportType={statusType}
          bomName={boms.find((b) => b.id === statusRow.bomId)?.name ?? ""}
          onClose={closeStatus}
          onGoToAssignment={goToAssignment}
          onOpenTraveler={(serial) =>
            history.push(`/project_management/virtual_traveler/${serial}`)
          }
        />
      )}

      {side?.kind === "history" && (
        <HistoryPanel
          reportId={side.reportId}
          entries={historyQuery.data}
          loading={historyQuery.isLoading}
          error={historyQuery.error}
          rows={rows}
          reportTypes={reportTypes}
          boms={boms}
          busy={workspace.isMutating}
          onUndo={(id) => void workspace.undoChange(id)}
          onShowAll={() => setSide({ kind: "history", reportId: null })}
          onClose={closeSide}
        />
      )}

      {side?.kind === "rules" && (
        <DataReadyRulesPanel reportTypes={reportTypes} onClose={closeSide} />
      )}

      <div className={`toast${toast ? " on" : ""}`} role="status" aria-live="polite">
        {toast && (
          <>
            <span>{toast.message}</span>
            {toast.undo && (
              <button
                type="button"
                onClick={() => {
                  toast.undo?.();
                  setToast(null);
                }}
              >
                Undo
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
